import type { GraphData } from '@/types/graph'

type NodeType = 'symbol' | 'tag' | 'connection' | 'table_cell'

export interface NodePosition {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Where a node came from (ENG-2622). `detected` = produced by the parse/OCR
 * pipeline; `manual` = drawn by an annotator in the editor. Absent is treated
 * as `detected` (all pre-ENG-2622 nodes predate the flag), so the canvas can
 * style hand-drawn objects distinctly instead of making every box look detected.
 */
export type NodeSource = 'manual' | 'detected'

export interface NodeBase {
  id: string
  type: NodeType
  // Model detection confidence (0–1). This is a *model prediction* and must NOT
  // be overwritten by human approval — approval is tracked by `approved` so the
  // model's confidence stays visible for the annotator to validate against (ENG-2622).
  confidence?: number
  // SigLIP trust score (0–1, VISN-291): composed HITL review-priority signal
  // (class-relative confidence + competing-class margin + same-package anchor
  // similarity). The Review layer prefers it over raw `confidence` for
  // flagging; the raw confidence stays untouched and visible.
  trust?: number
  // Runner-up SigLIP class (VISN-291) — powers the reviewer's "possible: X"
  // hint / two-option quick-fix for near-tie flags.
  siglip_second_type?: string
  // True once an annotator has explicitly approved the node. Distinct from
  // `confidence`: approval is a human action, detection confidence is a model
  // output. Use `isNodeApproved()` to read it. (ENG-2622)
  approved?: boolean
  // Manual (annotator-drawn) vs detected (pipeline). See NodeSource. (ENG-2622)
  source?: NodeSource
  // Position properties are optional when bounding box data is missing
  x?: number
  y?: number
  width?: number
  height?: number
  // Freeform polygon outline (ENG-2582). When present, the node is a traced
  // shape rather than a plain bounding box; x/y/width/height hold its bbox for
  // selection. Persisted as the POLYGON geometry_wkt ring on save.
  vertices?: Array<{ x: number; y: number }>
  // PIC001 classification — persisted on every node type
  pnid_level_i?: string
  pnid_level_ii?: string
  pnid_level_iii?: string
  // Free-text annotation available on all node types
  note?: string
  // Short identifier label (e.g. "69TE1120A") — for Tag nodes this maps to `content`
  label?: string
  // Annotation text — distinct from the identifier `label`/Tag; only meaningful for Annotation-category objects.
  annotation_text?: string
  // Extra properties from the backend that don't have dedicated fields, as well as
  // backend fields not otherwise persisted on save (e.g. symbol_type, category,
  // cross-sheet connector linking: connected_to_document_id, etc.)
  extraProperties?: Record<string, string>
}

export interface SymbolNode extends NodeBase {
  type: 'symbol'
  name: string
  symbol_type?: string
  category?: string
  // label and note are inherited from NodeBase
  // True when the symbol is an instrument (derived from Category = Instrumentation).
  // Drives the purple canvas colour; kept in sync from the annotator's Category.
  is_instrument?: boolean
}

export interface TagNode extends NodeBase {
  type: 'tag'
  content: string
  writtenAlong?: string // VISN-360: the pipe the flow trace placed this label on (it is written along it)
  ownerSymbolId?: string // VISN-360: the instrument the attribute assigner gave this reading to
}

export interface ConnectionNode extends NodeBase {
  type: 'connection'
  geometry_wkt?: string
  color?: string // Line color based on type (continuous/dashed/dotted)
  line_type?: string // Line type: 'continuous', 'dashed', 'dotted'
  lineNumber?: string // VISN-140: the pipe line number this segment belongs to (e.g. 12"-P-1712-3CA2P2)
}

export interface TableCellNode extends NodeBase {
  type: 'table_cell'
  name: string
  content?: string
  category?: string
  row?: number
  col?: number
  table_index?: number
  /** How many columns this cell occupies (merged header). Absent/1 = a single column.
   *  The coordinates it covers carry no cell of their own. */
  col_span?: number
}

export type Node = SymbolNode | TagNode | ConnectionNode | TableCellNode

export interface Edge {
  id?: string
  fromNodeId: string
  toNodeId: string
  relation?: string
  properties?: Record<string, string>
}

export interface Graph {
  nodes: Node[]
  edges: Edge[]
}

/**
 * True when an annotator has explicitly approved the node (ENG-2622). Approval
 * is a human action and is the SOLE source of truth here — deliberately NOT
 * inferred from `confidence`. The parse pipeline stamps many detections at
 * `confidence = 1.0`, so treating that as "approved" made Pending Review reflect
 * raw model state instead of the annotator's decisions (the core ENG-2622 bug).
 * Everything therefore starts as pending until a human approves it.
 */
export const isNodeApproved = (node: Pick<NodeBase, 'approved'>): boolean =>
  node.approved === true

/** True when the node was drawn by an annotator rather than the pipeline (ENG-2622). */
export const isNodeManual = (node: Pick<NodeBase, 'source'>): boolean => node.source === 'manual'

const category_abv_to_name: Record<string, string> = {
  cen: 'Centrifuges',
  com: 'Compressors',
  cru: 'Crushers',
  dry: 'Dryers',
  eqp: 'Equipment',
  fil: 'Filters',
  fit: 'Fittings',
  gen: 'Generators',
  htx: 'Heat Exchangers',
  ins: 'Instruments',
  inst: 'Instruments',
  ind: 'Indicators',
  ino: 'Inlet/Outlet',
  lin: 'Links',
  mix: 'Mixers',
  mot: 'Motors',
  pum: 'Pumps',
  per: 'Peripheral Equipment',
  pic: 'Piping Components',
  pip: 'Piping',
  sep: 'Separators',
  sig: 'Signals',
  val: 'Valves',
  valv: 'Valves',
  ves: 'Vessels',
}

const isRomanNumeral = (str: string): boolean => {
  return (
    str === 'i' ||
    str === 'ii' ||
    str === 'iii' ||
    str === 'iv' ||
    str === 'v' ||
    str === 'vi' ||
    str === 'vii' ||
    str === 'viii' ||
    str === 'ix' ||
    str === 'x'
  )
}

const capitalize = (str: string): string => {
  if (isRomanNumeral(str)) {
    return str.toUpperCase()
  }
  if (str.length > 0) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
  return str
}

export const parseName = (
  name: string
): { category: string; label: string } => {
  const nameParts = name.toLowerCase().split('_')
  let category = ''
  let startIdx = 0
  if (nameParts.length > 0 && category_abv_to_name[nameParts[0]]) {
    category = category_abv_to_name[nameParts[0]]
    startIdx = 1

    if (nameParts.length > 1 && category_abv_to_name[nameParts[1]]) {
      category = category_abv_to_name[nameParts[1]]
      startIdx = 2
    }
  }

  const label = nameParts
    .slice(startIdx)
    .map((part) => capitalize(part))
    .join(' ')
  return { category, label }
}

export const parseGeometryWkt = (geometryWkt: string): NodePosition | null => {
  let coordinates: number[][] = []

  if (geometryWkt.startsWith('POLYGON')) {
    const coordsMatch = geometryWkt.match(/POLYGON \(\((.*?)\)\)/)
    if (!coordsMatch) return null

    coordinates = coordsMatch[1]
      .split(',')
      .map((coord) => coord.trim().split(' ').map(Number))
  } else if (geometryWkt.startsWith('MULTILINESTRING')) {
    const coordsMatch = geometryWkt.match(/MULTILINESTRING \((.*?)\)/)
    if (!coordsMatch) return null
    // Extract all coordinate pairs from each linestring in the multilinestring
    coordinates = coordsMatch[1]
      .split(/\),\s*\(/) // Split different linestrings
      .map((lineString) => lineString.replace(/[()]/g, '')) // Remove remaining parentheses
      .flatMap((lineString) =>
        lineString
          .split(',')
          .map((coord) => coord.trim().split(' ').map(Number))
      )
  } else if (geometryWkt.startsWith('LINESTRING')) {
    const coordsMatch = geometryWkt.match(/LINESTRING \((.*?)\)/)
    if (!coordsMatch) return null
    coordinates = coordsMatch[1]
      .split(',')
      .map((coord) => coord.trim().split(' ').map(Number))
  } else if (geometryWkt.startsWith('POINT')) {
    // Handle POINT geometry: "POINT (x y)"
    const match = geometryWkt.match(/POINT\s*\(\s*([\d.-]+)\s+([\d.-]+)\s*\)/)
    if (!match) return null
    const x = parseFloat(match[1])
    const y = parseFloat(match[2])
    // Create a small 10x10 bounding box around the point
    return { x: x - 5, y: y - 5, width: 10, height: 10 }
  } else {
    return null // Unsupported geometry type
  }

  // Find min/max coordinates to create bounding box
  const xCoords = coordinates.map((coord) => coord[0])
  const yCoords = coordinates.map((coord) => coord[1])
  const x = Math.min(...xCoords)
  const y = Math.min(...yCoords)
  let width = Math.max(...xCoords) - x
  let height = Math.max(...yCoords) - y
  if (width < 1) width = 1
  if (height < 1) height = 1

  // For LINESTRINGs and MULTILINESTRINGs, ensure minimum dimensions
  const isLineType =
    geometryWkt.startsWith('LINESTRING') ||
    geometryWkt.startsWith('MULTILINESTRING')
  const finalWidth = isLineType && width === 1 ? 10 : width
  const finalHeight = isLineType && height === 1 ? 10 : height

  return { x, y, width: finalWidth, height: finalHeight }
}

// Extract the traced outline of a freeform POLYGON (ENG-2582), excluding the
// closing duplicate point. Returns null for non-polygons and for plain
// axis-aligned rectangles — those are represented by the x/y/width/height bbox,
// so there's nothing freeform to preserve.
export const parsePolygonVertices = (
  geometryWkt: string,
): Array<{ x: number; y: number }> | null => {
  if (!geometryWkt.startsWith('POLYGON')) return null
  // Capture only the first (outer) ring so an interior hole — e.g.
  // POLYGON ((outer…), (inner…)) — doesn't bleed into the outer vertices.
  const coordsMatch = geometryWkt.match(/POLYGON\s*\(\(([^)]*)\)/)
  if (!coordsMatch) return null

  const pts = coordsMatch[1]
    .split(',')
    .map((coord) => {
      const [x, y] = coord.trim().split(/\s+/).map(Number)
      return { x, y }
    })
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))

  // Drop the closing point if it repeats the first.
  if (pts.length >= 2) {
    const first = pts[0]
    const last = pts[pts.length - 1]
    if (first.x === last.x && first.y === last.y) pts.pop()
  }
  if (pts.length < 3) return null

  // A 4-corner, axis-aligned ring is just a bounding box — not a freeform shape.
  if (pts.length === 4) {
    const xs = new Set(pts.map((p) => p.x))
    const ys = new Set(pts.map((p) => p.y))
    if (xs.size <= 2 && ys.size <= 2) return null
  }
  return pts
}

// Serialize a freeform polygon's vertices into a closed POLYGON ring,
// appending the first point as the closing vertex. Inverse of
// parsePolygonVertices.
export const verticesToPolygonWkt = (
  vertices: Array<{ x: number; y: number }>,
): string => {
  const ring = vertices.map((v) => `${v.x} ${v.y}`).join(', ')
  const first = vertices[0]
  return `POLYGON ((${ring}, ${first.x} ${first.y}))`
}


/**
 * Parse a (MULTI)LINESTRING into its sub-segments, **preserving** the
 * boundaries between them — one `[x, y][]` per sub-segment.
 *
 * Callers that STROKE the geometry must keep sub-segments apart: a single
 * polyline drawn across all of them bridges the end of one segment to the
 * start of the next, painting a phantom corner-to-corner diagonal across
 * P&ID/PFD thumbnails. (`parseLineCoordinates` deliberately flattens for
 * anchor/bbox use; this one must not — that is the whole point of it.)
 */
export const parseLineStringSegments = (geometryWkt: string): number[][][] => {
  if (!geometryWkt) return []
  const match = geometryWkt.match(/(?:MULTI)?LINESTRING\s*\(?(.*?)\)?$/)
  if (!match) return []
  const segments: number[][][] = []
  // A MULTILINESTRING delimits disjoint sub-segments with "),("; a plain
  // LINESTRING has no such boundary and yields a single chunk.
  for (const chunk of match[1].split(/\)\s*,\s*\(/)) {
    const pairs = chunk
      .replace(/[()]/g, '')
      .split(',')
      .map((c) => c.trim().split(/\s+/).map(Number))
      .filter((p) => p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    if (pairs.length >= 2) segments.push(pairs)
  }
  return segments
}

const parseLineCoordinates = (
  geometryWkt?: string
): { x: number; y: number }[] | null => {
  if (!geometryWkt) return null

  const normaliseCoord = (coord: string): { x: number; y: number } | null => {
    const parts = coord.trim().split(/\s+/)
    if (parts.length < 2) return null
    const x = Number(parts[0])
    const y = Number(parts[1])
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null
    return { x, y }
  }

  if (geometryWkt.startsWith('LINESTRING')) {
    const coordsMatch = geometryWkt.match(/LINESTRING\s*\((.*?)\)/)
    if (!coordsMatch) return null
    const coords = coordsMatch[1]
      .split(',')
      .map((coord) => normaliseCoord(coord))
      .filter((coord): coord is { x: number; y: number } => coord !== null)
    return coords.length ? coords : null
  }

  if (geometryWkt.startsWith('MULTILINESTRING')) {
    const coordsMatch = geometryWkt.match(/MULTILINESTRING\s*\((.*)\)/)
    if (!coordsMatch) return null
    const segments = coordsMatch[1].split(/\),\s*\(/)
    const coords: { x: number; y: number }[] = []
    for (const segment of segments) {
      const cleaned = segment.replace(/[()]/g, '')
      const pairs = cleaned
        .split(',')
        .map((coord) => normaliseCoord(coord))
        .filter((coord): coord is { x: number; y: number } => coord !== null)
      coords.push(...pairs)
    }
    return coords.length ? coords : null
  }

  return null
}

const parseLineAnchor = (
  geometryWkt: string
): { x: number; y: number } | null => {
  const coords = parseLineCoordinates(geometryWkt)
  return coords && coords.length ? coords[0] : null
}

const parseIdList = (value?: string): string[] | null => {
  if (!value) return null
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item))
    }
  } catch {
    // fall through
  }

  const parts = value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  return parts.length ? parts : null
}

// Properties that are handled explicitly during node construction.
// Anything not in this set is collected into node.extraProperties.
const KNOWN_PROPERTY_KEYS = new Set([
  'type', 'geometry_wkt', 'confidence', 'name',
  'trust', 'siglip_margin', 'siglip_second_type', // VISN-291: SigLIP trust signals — read-only model outputs, keep out of extraProperties
  'approved', // ENG-2622: human approval state (kept separate from model confidence)
  'source', // ENG-2622: manual-vs-detected provenance
  'replaced_nodes', 'carried_to', // written by the link carry on approve; the editor never writes them back
  'label', 'note', 'annotation_text', 'content', 'point_ids', 'line_type', 'color',
  'line_number', 'line_number_confidence', // VISN-140: read-only extraction outputs — keep out of extraProperties so they aren't persisted back on save
  'written_along', 'owner_symbol_id', // VISN-360: which rule linked a label — read-only, same reason
  'is_instrument',
  'pnid_level_i', 'pnid_level_ii', 'pnid_level_iii',
  'centroid_x', 'centroid_y',
  'row', 'col', 'table_index', 'col_span',
])

export const parseGraph = (graph: GraphData): Graph => {
  // Create a map of properties grouped by target_id for efficient lookup
  const propertiesByTarget = Object.values(graph.properties).reduce(
    (acc, prop) => {
      if (!acc[prop.target_id]) {
        acc[prop.target_id] = []
      }
      acc[prop.target_id].push(prop)
      return acc
    },
    {} as Record<
      string,
      (typeof graph.properties)[keyof typeof graph.properties][]
    >
  )

  // Collect extra (unknown) properties for a node into a Record.
  const collectExtraProperties = (
    propsMap: Record<string, string>
  ): Record<string, string> | undefined => {
    const extra: Record<string, string> = {}
    for (const [key, value] of Object.entries(propsMap)) {
      if (!KNOWN_PROPERTY_KEYS.has(key) && value != null && value !== 'null') {
        extra[key] = value
      }
    }
    return Object.keys(extra).length > 0 ? extra : undefined
  }

  // Convert nodes from Set to array of Node objects
  const nodes: Node[] = []
  const nodeCoords: Record<string, { x: number; y: number }> = {}
  const pendingConnections: {
    node: ConnectionNode
    pointIds?: string[]
    fallbackAnchor?: { x: number; y: number } | null
    geometryWkt?: string
  }[] = []

  for (const nodeId of graph.nodes) {
    const nodeProperties = propertiesByTarget[nodeId] || []

    const nodePropertiesMap = nodeProperties.reduce((acc, prop) => {
      acc[prop.key] = prop.value
      return acc
    }, {} as Record<string, string>)

    const geometryWkt = nodePropertiesMap['geometry_wkt']
    let bbox:
      | { x: number; y: number; width: number; height: number }
      | undefined

    if (geometryWkt) {
      const parsedBbox = parseGeometryWkt(geometryWkt)
      if (!parsedBbox) {
        console.error(
          `Node ${nodeId} has invalid geometry_wkt property: ${geometryWkt}`
        )
        continue
      }
      bbox = parsedBbox
    } else {
      console.warn(
        `Node ${nodeId} is missing geometry_wkt property - will not be displayed in editor mode, only in graph preview`
      )
    }

    const fallbackAnchor = geometryWkt ? parseLineAnchor(geometryWkt) : null
    const polygonVertices = geometryWkt ? parsePolygonVertices(geometryWkt) : null

    let centroid: { x: number; y: number } | undefined
    if (bbox) {
      centroid = {
        x: bbox.x + bbox.width / 2,
        y: bbox.y + bbox.height / 2,
      }
    } else if (fallbackAnchor) {
      centroid = fallbackAnchor
    }

    const nodeType = nodePropertiesMap['type']
    if (!nodeType) {
      console.error(`Node ${nodeId} is missing type property`)
      continue
    }

    const confidenceValue = nodePropertiesMap['confidence']
    // Same guard as trust below: a 'null'/non-numeric stored value must fall
    // back to undefined, never NaN (NaN < cutoff is false -> unflaggable node).
    const confidenceParsed = confidenceValue && confidenceValue !== 'null' ? parseFloat(confidenceValue) : NaN
    const confidence = Number.isFinite(confidenceParsed) ? confidenceParsed : undefined

    // VISN-291: composed SigLIP trust score; absent on parses that predate it.
    const trustValue = nodePropertiesMap['trust']
    const trustParsed = trustValue && trustValue !== 'null' ? parseFloat(trustValue) : NaN
    // Non-numeric stored trust must fall back to confidence-based flagging,
    // not become NaN (NaN < cutoff is false -> node could never be flagged).
    const trust = Number.isFinite(trustParsed) ? trustParsed : undefined
    const secondRaw = nodePropertiesMap['siglip_second_type']
    const siglip_second_type = secondRaw && secondRaw !== 'null' ? secondRaw : undefined

    // ENG-2622: human approval, tracked separately from the model's confidence.
    // Only ever persisted as 'true'; absence means not-yet-approved.
    const approved = nodePropertiesMap['approved'] === 'true' ? true : undefined
    // ENG-2622: manual/detected provenance. Absence means detected (all pipeline
    // nodes predate the flag).
    const source: NodeSource | undefined =
      nodePropertiesMap['source'] === 'manual'
        ? 'manual'
        : nodePropertiesMap['source'] === 'detected'
          ? 'detected'
          : undefined

    const pnid_level_i = nodePropertiesMap['pnid_level_i'] || undefined
    const pnid_level_ii = nodePropertiesMap['pnid_level_ii'] || undefined
    const pnid_level_iii = nodePropertiesMap['pnid_level_iii'] || undefined

    if (nodeType === 'tag') {
      const content = nodePropertiesMap['content'] || ''  // Allow empty content
      const tagNoteRaw = nodePropertiesMap['note']
      const tagNote = tagNoteRaw && tagNoteRaw !== 'null' ? tagNoteRaw : undefined

      const node: TagNode = {
        id: nodeId,
        type: 'tag',
        content,
        confidence,
        trust,
        siglip_second_type,
        approved,
        source,
        note: tagNote,
        annotation_text: nodePropertiesMap['annotation_text'] || undefined,
        writtenAlong: nodePropertiesMap['written_along'] || undefined,
        ownerSymbolId: nodePropertiesMap['owner_symbol_id'] || undefined,
        pnid_level_i,
        pnid_level_ii,
        pnid_level_iii,
        ...(bbox || {}),
        extraProperties: collectExtraProperties(nodePropertiesMap),
      }
      nodes.push(node)
      if (centroid) nodeCoords[nodeId] = centroid
    } else if (nodeType === 'symbol') {
      const name = nodePropertiesMap['name']
      const symbol_type = nodePropertiesMap['symbol_type']
      const label = nodePropertiesMap['label']  // Read label from backend
      const category = nodePropertiesMap['category']  // Read category from backend
      // Read note from backend - handle "null" string as undefined
      const noteRaw = nodePropertiesMap['note']
      const note = noteRaw && noteRaw !== 'null' ? noteRaw : undefined

      if (!name) {
        console.error(`Symbol node ${nodeId} is missing name property`)
        continue
      }

      // Symbol is an instrument when the is_instrument property is set (derived
      // from Category = Instrumentation at annotation time).
      const is_instrument = nodePropertiesMap['is_instrument'] === 'true'

      const node: SymbolNode = {
        id: nodeId,
        type: 'symbol',
        name,
        symbol_type,
        category,  // Include category from backend
        label,  // Include label from backend (populated from has_label edges)
        note,  // Include note from backend (additional text for instruments)
        annotation_text: nodePropertiesMap['annotation_text'] || undefined,
        confidence,
        trust,
        siglip_second_type,
        approved,
        source,
        pnid_level_i,
        pnid_level_ii,
        pnid_level_iii,
        ...(bbox || {}),
        // Symbols only (ENG-2582): user-traced polygons are symbols, so they
        // round-trip as freeform shapes. Parsed freeform POLYGONs are all
        // text-region tags — those intentionally stay bounding boxes.
        ...(polygonVertices ? { vertices: polygonVertices } : {}),
        ...(is_instrument && { is_instrument: true }),
        extraProperties: collectExtraProperties(nodePropertiesMap),
      }
      nodes.push(node)
      if (centroid) nodeCoords[nodeId] = centroid
    } else if (nodeType === 'connection') {
      const pointIds = parseIdList(nodePropertiesMap['point_ids'])
      const lineType = nodePropertiesMap['line_type']
      const color = nodePropertiesMap['color']
      const connLabel = nodePropertiesMap['label'] || undefined
      const connNoteRaw = nodePropertiesMap['note']
      const connNote = connNoteRaw && connNoteRaw !== 'null' ? connNoteRaw : undefined
      const connLineNumberRaw = nodePropertiesMap['line_number']
      const connLineNumber =
        connLineNumberRaw && connLineNumberRaw !== 'null' ? connLineNumberRaw : undefined
      const node: ConnectionNode = {
        id: nodeId,
        type: 'connection',
        confidence,
        trust,
        siglip_second_type,
        approved,
        source,
        label: connLabel,
        note: connNote,
        annotation_text: nodePropertiesMap['annotation_text'] || undefined,
        pnid_level_i,
        pnid_level_ii,
        pnid_level_iii,
        ...(bbox || {}),
        geometry_wkt: geometryWkt,
        line_type: lineType,
        color: color,
        lineNumber: connLineNumber,
        extraProperties: collectExtraProperties(nodePropertiesMap),
      }
      nodes.push(node)
      pendingConnections.push({
        node,
        pointIds: pointIds || undefined,
        fallbackAnchor,
        geometryWkt: geometryWkt || undefined,
      })
    } else if (nodeType === 'instrument') {
      // Convert instrument nodes to symbol nodes with is_instrument flag
      const node: SymbolNode = {
        id: nodeId,
        type: 'symbol',
        name: nodePropertiesMap['name'] || nodePropertiesMap['symbol_type'] || 'Instrument',
        symbol_type: nodePropertiesMap['symbol_type'],
        is_instrument: true,
        confidence,
        trust,
        siglip_second_type,
        approved,
        source,
        annotation_text: nodePropertiesMap['annotation_text'] || undefined,
        ...(bbox || {}),
        extraProperties: collectExtraProperties(nodePropertiesMap),
      }
      nodes.push(node)
      if (centroid) nodeCoords[nodeId] = centroid
    } else if (nodeType === 'table_cell') {
      const name = nodePropertiesMap['name'] || ''
      const content = nodePropertiesMap['content'] || name
      const category = nodePropertiesMap['category']
      const row = nodePropertiesMap['row'] ? parseInt(nodePropertiesMap['row']) : undefined
      const col = nodePropertiesMap['col'] ? parseInt(nodePropertiesMap['col']) : undefined
      const table_index = nodePropertiesMap['table_index'] ? parseInt(nodePropertiesMap['table_index']) : undefined
      const colSpanRaw = nodePropertiesMap['col_span'] ? parseInt(nodePropertiesMap['col_span']) : NaN
      const col_span = Number.isFinite(colSpanRaw) && colSpanRaw > 1 ? colSpanRaw : undefined

      const tableCellLabel = nodePropertiesMap['label'] || undefined
      const tableCellNoteRaw = nodePropertiesMap['note']
      const tableCellNote = tableCellNoteRaw && tableCellNoteRaw !== 'null' ? tableCellNoteRaw : undefined

      const node: TableCellNode = {
        id: nodeId,
        type: 'table_cell',
        name,
        content,
        category,
        confidence,
        trust,
        siglip_second_type,
        label: tableCellLabel,
        note: tableCellNote,
        annotation_text: nodePropertiesMap['annotation_text'] || undefined,
        pnid_level_i,
        pnid_level_ii,
        pnid_level_iii,
        ...(bbox || {}),
        row,
        col,
        table_index,
        col_span,
        extraProperties: collectExtraProperties(nodePropertiesMap),
      }
      nodes.push(node)
      if (centroid) nodeCoords[nodeId] = centroid
    } else if (nodeType === 'junction') {
      if (centroid) nodeCoords[nodeId] = centroid
      continue
    } else {
      console.error(`Node ${nodeId} has invalid type property: ${nodeType}`)
      if (centroid) nodeCoords[nodeId] = centroid
    }
  }

  for (const pending of pendingConnections) {
    const { node, pointIds, fallbackAnchor, geometryWkt } = pending
    const coords: { x: number; y: number }[] = []
    if (pointIds) {
      for (const pid of pointIds) {
        const coord = nodeCoords[pid]
        if (coord) {
          coords.push(coord)
        }
      }
    }

    if (coords.length > 0) {
      const meanX = coords.reduce((sum, coord) => sum + coord.x, 0) / coords.length
      const meanY = coords.reduce((sum, coord) => sum + coord.y, 0) / coords.length
      node.x = meanX
      node.y = meanY
      node.width = 1
      node.height = 1
      nodeCoords[node.id] = { x: meanX, y: meanY }
    } else {
      const lineCoords = parseLineCoordinates(geometryWkt)
      if (lineCoords && lineCoords.length) {
        const meanX = lineCoords.reduce((sum, coord) => sum + coord.x, 0) / lineCoords.length
        const meanY = lineCoords.reduce((sum, coord) => sum + coord.y, 0) / lineCoords.length
        node.x = meanX
        node.y = meanY
        node.width = 1
        node.height = 1
        nodeCoords[node.id] = { x: meanX, y: meanY }
      } else if (fallbackAnchor) {
        node.x = fallbackAnchor.x
        node.y = fallbackAnchor.y
        node.width = 1
        node.height = 1
        nodeCoords[node.id] = fallbackAnchor
      }
    }
  }

  // Build edge properties lookup: edgeId -> { key: value }
  const edgePropsMap: Record<string, Record<string, string>> = {}
  for (const prop of Object.values(graph.properties)) {
    if (!prop.is_node_property && prop.target_id) {
      if (!edgePropsMap[prop.target_id]) {
        edgePropsMap[prop.target_id] = {}
      }
      edgePropsMap[prop.target_id][prop.key] = prop.value
    }
  }

  // Convert edges from Record to array, attaching properties
  const edges: Edge[] = Object.values(graph.edges).map((edge) => {
    const props = edgePropsMap[edge.id] || {}
    return {
      id: edge.id,
      fromNodeId: edge.from_node,
      toNodeId: edge.to_node,
      relation: props.relation,
      properties: Object.keys(props).length > 0 ? props : undefined,
    }
  })

  // A pipe's line-number text is Annotation ▸ Text ▸ Attribute, not a Tag
  // (Sachin/Armin). The pipeline (VISN-140) already assigns each pipe's valid line
  // number to the connection's `line_number` (parsed onto ConnectionNode.lineNumber);
  // the same text also exists as a `tag` node linked to that connection via an
  // `annotated_by` edge. Match ONLY that text node — the one whose content carries
  // its own connection's line number — and classify it Attribute. Other pipe text
  // (notes, titles, dimensions) is left untouched for now (handled separately), and
  // we never override a node that already has a stored classification.
  const normalizeLine = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const nodeById = new Map(nodes.map((n) => [n.id, n]))
  for (const edge of edges) {
    if (edge.relation !== 'annotated_by') continue
    const ends = [nodeById.get(edge.fromNodeId), nodeById.get(edge.toNodeId)]
    const conn = ends.find((n) => n?.type === 'connection') as ConnectionNode | undefined
    const text = ends.find((n) => n?.type === 'tag') as TagNode | undefined
    const lineNo = conn?.lineNumber
    if (!text || text.pnid_level_i || !lineNo) continue
    const normLine = normalizeLine(lineNo)
    if (normLine.length >= 5 && normalizeLine(text.content ?? '').includes(normLine)) {
      text.pnid_level_i = 'Annotation'
      text.pnid_level_ii = 'Text'
      text.pnid_level_iii = 'Attribute'
    }
  }

  return { nodes, edges }
}
